import { IApiBaseProps, ISortByQuery } from '@common/api.models';
import { createSearchAsyncFn } from '@common/search.services';
import { createGetOptionsWithSearchFn } from '@common/selectOptions.services';
import { envConfigs } from '@config/envConfigs';
import { createDownloadFileWithCustomPropsAsyncFn } from '@file/file.services';
import { IPagedResultsV1 } from '@gcl/react-core';
import { ETransmittalType } from '@transmittal/transmittal.constants';
import { IFileResponse } from '@file/projectFile/projectFile.models';
import { apiServerGet, apiServerPost } from '@common/axios.services';
import { IDownloadFileBaseProps } from '@file/file.models';
import {
  IMasterScheduleOption,
  IMasterScheduleSearchFilter,
  IMasterScheduleSearchResult,
  IMasterScheduleTwcOptionsForLoadingRemovalCertRequest,
  IProjectMasterScheduleOptionWithProjectTeamMasterScheduleRequest,
  IProjectMasterScheduleOptionWithRoles,
  IProjectMasterScheduleQrPdfWithIdRequest,
  IProjectMasterScheduleTwcOption,
  IProjectTeamMasterScheduleAssignProjectMasterScheduleOption,
  IUpsertProjectTeamMasterScheduleRequest,
} from './masterSchedule.models';
import { ICreateBatchProjectMasterScheduleRequest } from './masterScheduleBatchAdd.models';

export const apiRoutes = {
  searchMasterSchedule: '/MasterSchedules/Search',
  exportMasterScheduleSearchResult: '/MasterSchedules/ExportExcel',
  exportMasterScheduleSearchToPdfResult: '/MasterSchedules/ExportPdf',
  searchMasterScheduleOptions: '/MasterSchedules/Options/Search',
  getMasterScheduleOptionsForTransmittal: '/MasterSchedules/OptionsForTransmittal',
  getMasterScheduleOptionsForDesignModification: '/MasterSchedules/OptionsForDesignModification',
  getMasterScheduleOptionsForLoadingRemovalCert: '/MasterSchedules/OptionsForLoadingRemovalCert',
  getMasterScheduleOptionsForDesignBrief: '/MasterSchedules/OptionsForDesignBrief',
  getMasterScheduleOptionsForAssignT1Teams: '/MasterSchedules/OptionsForAssignT1Teams',
  getMasterScheduleTwcOptionsForLoadingRemovalCert:
    '/MasterSchedules/ProjectMasterScheduleTwcs/OptionsForLoadingRemovalCert',
  upsertProjectTeamMasterSchedules: '/MasterSchedules/ProjectTeamMasterSchedules/Upsert',
  exportMasterScheduleQrcodeWithId: (id: number) => `/MasterSchedules/${id}/QRPdf`,
  exportMasterScheduleAllQrcode: '/MasterSchedules/QRPdf/All',
  batchAdd: '/MasterSchedules/BatchAdd',
};

export const searchMasterScheduleAsync = createSearchAsyncFn<IMasterScheduleSearchFilter, IMasterScheduleSearchResult>(
  apiRoutes.searchMasterSchedule,
);

export interface IExportProjectMasterScheduleProps extends IDownloadFileBaseProps {
  sortByQuery?: ISortByQuery;
  searchFilter: IMasterScheduleSearchFilter;
}

export const exportProjectMasterScheduleSearchExcelAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportProjectMasterScheduleProps>(
    apiRoutes.exportMasterScheduleSearchResult,
  );

export const exportProjectMasterScheduleSearchPdfAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportProjectMasterScheduleProps>(
    apiRoutes.exportMasterScheduleSearchToPdfResult,
  );

export const exportProjectMasterScheduleQrPdfAllAsync =
  createDownloadFileWithCustomPropsAsyncFn<IDownloadFileBaseProps>(apiRoutes.exportMasterScheduleAllQrcode);

export const exportProjectMasterScheduleQrPdfWithIdAsync = async (
  props: IProjectMasterScheduleQrPdfWithIdRequest,
): Promise<IFileResponse> => {
  const { onDownloadProgressUpdate, ...request } = props;
  const fileResponse = await apiServerPost<Blob>(
    `${envConfigs.apiBaseUrl}${apiRoutes.exportMasterScheduleQrcodeWithId(props.id)}`,
    request,
    {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (onDownloadProgressUpdate) {
          const { total, loaded } = progressEvent;

          const percentCompleted = Math.floor((loaded / total) * 100);
          onDownloadProgressUpdate(percentCompleted);
        }
      },
    },
  );

  let filename = '';
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(fileResponse.headers['content-disposition']);
  if (matches != null && matches[1]) {
    filename = matches[1].replace(/['"]/g, '');
  }
  return { file: fileResponse.data, filename };
};

export const getMasterScheduleOptionsWithSearchAsync = createGetOptionsWithSearchFn<IMasterScheduleOption>(
  apiRoutes.searchMasterScheduleOptions,
);

export interface IGetMasterScheduleOptionsForTransmittalAsyncProps extends IApiBaseProps {
  transmittalType?: ETransmittalType;
}

export const getMasterScheduleOptionsForTransmittalAsync = async (
  props?: IGetMasterScheduleOptionsForTransmittalAsyncProps,
): Promise<IPagedResultsV1<IProjectMasterScheduleOptionWithRoles>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectMasterScheduleOptionWithRoles>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getMasterScheduleOptionsForTransmittal}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const getMasterScheduleOptionsForDesignBriefAsync = async (
  props?: IApiBaseProps,
): Promise<IPagedResultsV1<IProjectMasterScheduleOptionWithRoles>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectMasterScheduleOptionWithRoles>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getMasterScheduleOptionsForDesignBrief}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const getMasterScheduleOptionsForDesignModificationAsync = async (
  props?: IApiBaseProps,
): Promise<IPagedResultsV1<IProjectMasterScheduleOptionWithRoles>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectMasterScheduleOptionWithRoles>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getMasterScheduleOptionsForDesignModification}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};
export const getMasterScheduleOptionsForLoadingRemovalCertAsync = async (
  props?: IApiBaseProps,
): Promise<IPagedResultsV1<IProjectMasterScheduleOptionWithRoles>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectMasterScheduleOptionWithRoles>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getMasterScheduleOptionsForLoadingRemovalCert}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const getMasterScheduleTwcOptionsForLoadingRemovalCertAsync = async (
  props?: IMasterScheduleTwcOptionsForLoadingRemovalCertRequest,
): Promise<IPagedResultsV1<IProjectMasterScheduleTwcOption>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectMasterScheduleTwcOption>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getMasterScheduleTwcOptionsForLoadingRemovalCert}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const getProjectMasterScheduleOptionsForAssignT1TeamsAsync = async (
  props?: IProjectMasterScheduleOptionWithProjectTeamMasterScheduleRequest,
): Promise<IProjectTeamMasterScheduleAssignProjectMasterScheduleOption> => {
  const result = await apiServerGet<IProjectTeamMasterScheduleAssignProjectMasterScheduleOption>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getMasterScheduleOptionsForAssignT1Teams}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const upsertProjectTeamMasterSchedulesAsync = async (request: IUpsertProjectTeamMasterScheduleRequest[]) => {
  const result = await apiServerPost(`${envConfigs.apiBaseUrl}${apiRoutes.upsertProjectTeamMasterSchedules}`, request);
  return result.data;
};

export const postProjectMasterSchedulesBatchAdd = async (request: ICreateBatchProjectMasterScheduleRequest) => {
  const result = await apiServerPost(`${envConfigs.apiBaseUrl}${apiRoutes.batchAdd}`, request);
  return result.data;
};
